import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { fetchPublicForm, incrementFormViews } from "../Backend/formsfunctions";
import FormHeader from "../Components/Form/components/FormHeader";
import FormBody from "../Components/Form/components/FormBody";
import {
  getRadius,
  getShadow,
  getFontFamily,
} from "../Components/Form/utils/cssVariableMapper";

const PublicFormPage = () => {
  const { formId } = useParams();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});

  // 1. Fetch Form Data
  useEffect(() => {
    const loadForm = async () => {
      try {
        setLoading(true);
        const data = await fetchPublicForm(formId);
        setFormData(data);
        const cookieName = `contacthq_${formId}`;
        const hasViewed = document.cookie
          .split("; ")
          .find((row) => row.startsWith(`${cookieName}=`));
        if (!hasViewed) {
          await incrementFormViews(formId);
          const expiry = new Date();
          expiry.setDate(expiry.getDate() + 30);
          document.cookie = `${cookieName}=true; expires=${expiry.toUTCString()}; path=/; SameSite=Lax`;
        }
      } catch (err) {
        setError(err.message || "Failed to load form");
      } finally {
        setLoading(false);
      }
    };

    if (formId) loadForm();
  }, [formId]);

  // 2. Iframe Resizing Logic
  useEffect(() => {
    if (!formData) return;

    const sendHeight = () => {
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: "resize", formId, height }, "*");
    };

    sendHeight();
    const resizeObserver = new ResizeObserver(sendHeight);
    resizeObserver.observe(document.body);

    return () => resizeObserver.disconnect();
  }, [formData, formId]);

  // 3. Handle Input Changes
  const handleFieldChange = (fieldId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleFormSubmit = async () => {};

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  if (error || !formData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-slate-800 p-4 text-center">
        <span className="material-icons text-4xl text-gray-400 mb-2">
          error_outline
        </span>
        <h1 className="text-xl font-bold">Form Not Found</h1>
        <p className="text-gray-500 mt-1">
          {error || "This form may have been deleted or is currently private."}
        </p>
      </div>
    );
  }

  // Destructure config for styling
  const { header, body, inputs, button, content, thankYou } = formData;

  const shadowClass = getShadow(body?.shadow || "none");
  const radiusClass = getRadius(body?.radius || "md");
  const borderClass = body?.border?.enabled
    ? `border`
    : "border border-transparent";
  const borderColor = body?.border?.enabled ? body.border.color : "transparent";
  const fontFamily = getFontFamily(body?.font);

  return (
    <div className="min-h-screen bg-gray-50/50 flex justify-center items-start overflow-y-auto p-3 sm:p-6">
      <div
        className={`w-full max-w-2xl bg-white transition-all duration-300 ${shadowClass} ${radiusClass} ${borderClass} overflow-hidden flex flex-col`}
        style={{
          fontFamily: fontFamily,
          borderColor: borderColor,
          height: "fit-content",
        }}
      >
        <FormHeader config={header} content={content} />

        <FormBody
          formData={formData}
          bodyConfig={body}
          inputsConfig={inputs}
          buttonConfig={button}
          fields={content.fields}
          thankYou={thankYou}
          isPreview={true}
          answers={answers}
          onFieldChange={handleFieldChange}
          onSubmit={handleFormSubmit}
        />
      </div>
    </div>
  );
};

export default PublicFormPage;
